import React, { useMemo } from "react";

import type { ContinuityDiagnostic, ContinuityStatus } from "./NarrativeSonics.types";

export interface ContinuityWarningOverlayProps {
  viewportStartSec: number;
  viewportEndSec: number;
  pxPerSec: number;
  diagnostics: ContinuityDiagnostic[];
}

function statusStyles(status: ContinuityStatus) {
  switch (status) {
    case "coherent":
      return {
        fill: "rgba(16,185,129,0.10)",
        edge: "rgba(52,211,153,0.18)",
        glow: "rgba(16,185,129,0.12)",
        label: "rgba(209,250,229,0.86)",
      };
    case "abrupt":
      return {
        fill: "rgba(244,63,94,0.12)",
        edge: "rgba(251,113,133,0.22)",
        glow: "rgba(244,63,94,0.12)",
        label: "rgba(255,228,230,0.92)",
      };
    case "gapped":
      return {
        fill: "rgba(14,165,233,0.12)",
        edge: "rgba(125,211,252,0.22)",
        glow: "rgba(14,165,233,0.12)",
        label: "rgba(224,242,254,0.92)",
      };
    case "dense":
      return {
        fill: "rgba(234,179,8,0.12)",
        edge: "rgba(250,204,21,0.22)",
        glow: "rgba(234,179,8,0.12)",
        label: "rgba(254,249,195,0.92)",
      };
    case "mismatched":
      return {
        fill: "rgba(168,85,247,0.12)",
        edge: "rgba(192,132,252,0.22)",
        glow: "rgba(168,85,247,0.12)",
        label: "rgba(243,232,255,0.92)",
      };
    case "unresolved":
      return {
        fill: "rgba(234,88,12,0.12)",
        edge: "rgba(249,115,22,0.22)",
        glow: "rgba(234,88,12,0.12)",
        label: "rgba(255,237,213,0.92)",
      };
    default:
      return {
        fill: "rgba(255,255,255,0.06)",
        edge: "rgba(255,255,255,0.12)",
        glow: "rgba(255,255,255,0.08)",
        label: "rgba(255,255,255,0.88)",
      };
  }
}

export default function ContinuityWarningOverlay({
  viewportStartSec,
  viewportEndSec,
  pxPerSec,
  diagnostics,
}: ContinuityWarningOverlayProps): JSX.Element | null {
  const visibleDiagnostics = useMemo(
    () => diagnostics.filter((diagnostic) => diagnostic.endSec >= viewportStartSec && diagnostic.startSec <= viewportEndSec),
    [diagnostics, viewportEndSec, viewportStartSec],
  );

  if (visibleDiagnostics.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-[30]" data-oc-continuity-warning-overlay="1" aria-hidden>
      <div className="absolute left-0 right-0 top-[64px] h-4 rounded-2xl border border-white/6 bg-white/[0.01]" />
      {visibleDiagnostics.map((diagnostic) => {
        const leftPx = Math.max(0, diagnostic.startSec * pxPerSec);
        const widthPx = Math.max(5, (diagnostic.endSec - diagnostic.startSec) * pxPerSec);
        const styles = statusStyles(diagnostic.status);
        return (
          <div
            key={diagnostic.id}
            className="absolute rounded-full border"
            style={{
              left: leftPx,
              top: 66,
              width: widthPx,
              height: diagnostic.status === "coherent" ? 6 : 8,
              borderColor: styles.edge,
              background: styles.fill,
              boxShadow: `0 0 0 1px ${styles.edge}, 0 6px 14px ${styles.glow}`,
            }}
          >
            {diagnostic.status !== "coherent" && widthPx >= 52 ? (
              <div className="truncate px-2 text-[8px] font-black uppercase tracking-[0.16em]" style={{ color: styles.label }}>
                {diagnostic.status}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}